import isEmpty from "lodash/isEmpty";
import isNil from "lodash/isNil";
import omit from "lodash/omit";
import pick from "lodash/pick";
import {
  FIELD_ASSIGNEE_ID_KEY,
  FIELD_ATTACHMENT_IDS_KEY,
  FIELD_AUDIENCES_KEY,
  FIELD_BOOKING_CATEGORY_ID_KEY,
  FIELD_COMMUNICATION_TYPE_KEY,
  FIELD_CUSTOM_FIELDS_KEY,
  FIELD_DESCRIPTION_KEY,
  FIELD_DEVELOPMENTAL_KEY,
  FIELD_ID_KEY,
  FIELD_INTEREST_QUESTIONS_KEY,
  FIELD_INTEREST_QUESTIONS_TOGGLE_KEY,
  FIELD_INTEREST_STATEMENT_ENABLED_KEY,
  FIELD_METADATA_KEY,
  FIELD_MIN_REQUIRED_COUNT_KEY,
  FIELD_NAME_KEY,
  FIELD_PARENT_ACTIVITY_ID_KEY,
  FIELD_PARENT_ACTIVITY_KEY,
  FIELD_POST_UNTIL_CHANGE_REASON_KEY,
  FIELD_POST_UNTIL_KEY,
  FIELD_PRIVACY_KEY,
  FIELD_SKILLS_FRAMEWORK_IDS_KEY,
  FIELD_STATE_KEY,
  FIELD_SUBTEMPLATE_KEY,
  FIELD_TEMPLATE_ID_KEY
} from "PFApp/constants/activity_form/activity_form_field_keys";
import { getPostUntilChangeReasonData } from "PFApp/helpers/activity_form/post_until_change_reason";
import { ActivityFormType } from "PFApp/types/activity_form/activity_form_schema";
import { Activity, ActivityPureCustomValue, AvailabilityRule, Metadata } from "PFCore/types";

export const convertAPICustomFieldsToFormValues = (
  customFields: Activity["custom_fields"]
): ActivityFormType["custom_fields"] =>
  customFields.reduce(
    (acc, customField) => ({
      ...acc,
      [customField.type.name]: customField.values
    }),
    {}
  );

const convertFormValueCustomFieldsToAPI = (customFields: ActivityFormType["custom_fields"]) =>
  Object.keys(customFields)
    .filter((name) => !isNil(customFields[name]))
    .map((name) => ({
      type: { name },
      values: customFields[name].map((value) =>
        pick(value, [...(value.created ? [] : ["id"]), "text", "value", "importance", "required_experience"])
      ) as ActivityPureCustomValue[]
    }));

export const convertAvailabilityToAPI = (availability?: Partial<AvailabilityRule>) => {
  const ranges = (availability?.ranges || []).filter(({ start, end }) => start || end);

  return isEmpty(ranges) ? {} : { ...availability, ranges };
};

const convertFormValuesMetadataToAPI = (metadata: Metadata) => ({
  ...metadata,
  availability: convertAvailabilityToAPI(metadata.availability)
});

type SanitizeFormDataForAPIParams = {
  values: ActivityFormType;
  isNew: boolean;
  fieldsBlacklist?: string[];
};

export const sanitizeFormDataForAPI = ({ values, isNew, fieldsBlacklist }: SanitizeFormDataForAPIParams) => {
  const interestQuestionnaireData: {
    interest_statement_enabled?: boolean;
    interest_questions?: string[];
  } = {};
  if (isNew) {
    interestQuestionnaireData.interest_statement_enabled = values[FIELD_INTEREST_STATEMENT_ENABLED_KEY];
    if (values[FIELD_INTEREST_QUESTIONS_TOGGLE_KEY]) {
      interestQuestionnaireData.interest_questions = values[FIELD_INTEREST_QUESTIONS_KEY].map((value) =>
        value.trim()
      ).filter((value) => !!value);
    }
  }

  return omit(
    {
      ...pick(
        values,
        FIELD_ID_KEY,
        FIELD_TEMPLATE_ID_KEY,
        FIELD_SUBTEMPLATE_KEY,
        FIELD_POST_UNTIL_KEY,
        FIELD_SKILLS_FRAMEWORK_IDS_KEY,
        FIELD_BOOKING_CATEGORY_ID_KEY,
        FIELD_ATTACHMENT_IDS_KEY,
        FIELD_COMMUNICATION_TYPE_KEY,
        FIELD_PRIVACY_KEY,
        FIELD_NAME_KEY,
        FIELD_DESCRIPTION_KEY,
        FIELD_MIN_REQUIRED_COUNT_KEY,
        FIELD_DEVELOPMENTAL_KEY,
        FIELD_ASSIGNEE_ID_KEY,
        FIELD_STATE_KEY
      ),
      post_until_change_reason: getPostUntilChangeReasonData(values[FIELD_POST_UNTIL_CHANGE_REASON_KEY]),
      custom_fields: convertFormValueCustomFieldsToAPI(values[FIELD_CUSTOM_FIELDS_KEY]),
      metadata: convertFormValuesMetadataToAPI(values[FIELD_METADATA_KEY]),
      parent_activity_id:
        values[FIELD_PARENT_ACTIVITY_ID_KEY] ?? (values[FIELD_PARENT_ACTIVITY_KEY]?.id || null),
      audiences: values[FIELD_AUDIENCES_KEY].map((audience) => omit(audience, "unconfirmed")),
      ...interestQuestionnaireData
    },
    fieldsBlacklist ?? []
  );
};
